/**
 * computeColumnAggregation
 *
 * The single column-aggregation rule, shared by the server aggregation worker
 * (`computeAggregations`), the client PDF totals
 * (`ArTableAggregations`), and the Form Derive projection.
 *
 * Formula invariant (SYD-2292): a column a formula targets aggregates the
 * result the caller computed for that row (`formulaResults`), never the
 * persisted `calcInput` — a trigger-owned cache that may lag or, on a
 * `suppressFormulas` row, contradict the writer-set `value`. `formulaResults`
 * is absent exactly when the engine skipped the row, and then the stored value
 * is truth. `calcOutput` (kit roll-ups) is still read as stored.
 */

import type { ArColumnAggregation, TypeId } from '../System.Artifacts.types';

export type ArAggregationColumn = {
  typeId: string;
  aggregation: ArColumnAggregation;
  aggregationFieldPath?: string;
};

export type ArAggregationRow = {
  cells: Readonly<Record<string, { value?: unknown; calcOutput?: unknown }>> | undefined;
  /** Undefined exactly when the formula engine skipped the row; stored values then win. */
  formulaResults?: ReadonlyMap<TypeId, number | null>;
};

/**
 * The formula-sequence target this column aggregates, when it is one: a plain
 * column aggregates its own typeId, a composite column aggregating
 * `fields.<sub>.value` aggregates the dotted target `<typeId>.<sub>` — the same
 * spelling the engine uses. Undefined for any other path, which no formula can
 * write. Derived here rather than by the caller, so the two cannot desync.
 */
function resolveFormulaTypeId(typeId: string, aggregationFieldPath: string | undefined): string | undefined {
  if (!aggregationFieldPath) return typeId;
  const segments = aggregationFieldPath.split('.');
  if (segments.length !== 3 || segments[0] !== 'fields' || segments[2] !== 'value') return undefined;
  return `${typeId}.${segments[1]}`;
}

function resolveFieldPath(value: unknown, fieldPath: string): unknown {
  let current: unknown = value;
  for (const segment of fieldPath.split('.')) {
    if (current === null || current === undefined || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[segment];
  }
  return current;
}

function getNumericValue(value: unknown, calcOutput: unknown): number | null {
  if (typeof calcOutput === 'number' && Number.isFinite(calcOutput)) return calcOutput;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function computeColumnAggregation(
  column: ArAggregationColumn,
  rows: readonly ArAggregationRow[],
): number | null {
  const isCount = column.aggregation === 'count';
  const formulaTypeId = resolveFormulaTypeId(column.typeId, column.aggregationFieldPath);
  const values: number[] = [];
  let count = 0;

  for (const row of rows) {
    const cell = row.cells?.[column.typeId];
    const computed = formulaTypeId
      ? row.formulaResults?.get(formulaTypeId as TypeId)
      : undefined;
    const resolvedValue = column.aggregationFieldPath
      ? resolveFieldPath(cell?.value, column.aggregationFieldPath)
      : cell?.value;

    if (isCount) {
      if (computed !== undefined ? computed !== null : resolvedValue !== null && resolvedValue !== undefined) {
        count += 1;
      }
      continue;
    }

    const numeric = computed !== undefined ? computed : getNumericValue(resolvedValue, cell?.calcOutput);
    if (numeric !== null) values.push(numeric);
  }

  if (isCount) return count;
  if (values.length === 0) return null;
  if (column.aggregation === 'sum') return values.reduce((total, v) => total + v, 0);
  if (column.aggregation === 'avg') return values.reduce((acc, v) => acc + v, 0) / values.length;
  if (column.aggregation === 'min') return values.reduce((min, v) => (v < min ? v : min), values[0]);
  if (column.aggregation === 'max') return values.reduce((max, v) => (v > max ? v : max), values[0]);
  return null;
}

export { computeColumnAggregation };
